import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subject, Subscription, of, timer } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { Permissions } from '@app/core/guard/Permissions';
import { AccountingDataStandardizationService } from '@services/http/accounting-data-standardization/accounting-data-standardization.service';
import { CompanyStandardizationProgress } from '@interfaces/accounting-data-standardization/responses/company-standardization-progress.response';
import { PermissionsService } from '@services/helpers/permissions.service';
import { SharedModule } from '@shared/shared.module';
import { DfpStandardizationNavigationService } from '@src/app/features/dashboard/performance/pages/dfp/services/dfp-standardization-navigation.service';

/**
 * Estado vazio da Análise Econômica (DFP Histórica) e da Modelagem DFP (`financial-modeling/dre`): a tela tem uma empresa escolhida, a carga terminou e não
 * há número nenhum para mostrar.
 *
 * Concentra TODAS as redações de tela vazia da página. Extraído do `dfp.component` por duas razões:
 * o componente estava no teto de ~1.000 linhas do `AGENTS.md`, e os estilos do cartão
 * (`.dfp-empty-state__*`) precisavam viver junto do markup para não depender do encapsulamento do pai.
 *
 * ## Por que existem quatro redações
 *
 * O usuário age diferente em cada motivo, e mandá-lo para o lugar errado é pior que não dizer nada:
 *
 * | Motivo                            | O que falta                  | Atalho          |
 * | --------------------------------- | ---------------------------- | --------------- |
 * | padronização em andamento         | tempo (a IA está extraindo)  | nenhum: espera  |
 * | nenhum documento enviado          | envio                        | Data Room       |
 * | documento enviado, nada validado  | extração/validação           | Padronização    |
 * | não foi possível apurar           | (redação neutra)             | Padronização    |
 *
 * A ordem importa: "em andamento" é um caso ESPECÍFICO de "enviado mas não validado" e precisa ser testado
 * primeiro, senão o usuário que acabou de subir documento no Data Room recebe a mensagem genérica e vai
 * conferir uma tela de padronização que ainda não tem nada para ele.
 *
 * ## Polling
 *
 * Enquanto houver extração em voo, o componente reconsulta o resumo a cada 10s e, quando a fila termina,
 * emite `standardizationFinished` para a tela recarregar sozinha — o usuário não precisa dar F5 nem clicar.
 * **Sem timeout por decisão do dono do produto:** o polling vive enquanto este componente estiver na tela.
 * Como ele só é renderizado no estado vazio, sair da tela ou os dados aparecerem já o encerram.
 */
@Component({
  selector: 'app-dfp-empty-state',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './dfp-empty-state.component.html',
  styleUrls: ['./dfp-empty-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DfpEmptyStateComponent implements OnChanges, OnDestroy {
  private readonly standardizationService = inject(AccountingDataStandardizationService);
  private readonly router = inject(Router);

  /** Saída para a Padronização, compartilhada com o menu de coluna de ano (ver `goToStandardization`). */
  private readonly standardizationNavigation = inject(DfpStandardizationNavigationService);

  private readonly destroy$ = new Subject<void>();

  /** Intervalo do polling do resumo de padronização, em ms. */
  private static readonly POLL_INTERVAL_MS = 10_000;

  /** Falhas seguidas do resumo que encerram o polling. */
  private static readonly MAX_CONSECUTIVE_FAILURES = 3;

  private pollSubscription: Subscription | null = null;

  /** Falhas consecutivas do resumo. Guarda contra polling eterno num endpoint quebrado. */
  private consecutiveFailures = 0;

  /** Empresa selecionada na tela. Trocar de empresa reapura tudo do zero. */
  @Input({ required: true }) companyId!: string;

  /**
   * O `/dfp/status` não pôde ser lido (resposta vazia). Cai numa redação neutra própria, herdada da tela
   * antes dos estados por motivo — é o único caso em que a mensagem fala de "avaliação pela equipe".
   */
  @Input() statusUnavailable = false;

  /** A fila de padronização terminou enquanto o usuário esperava: a tela deve recarregar. */
  @Output() standardizationFinished = new EventEmitter<void>();

  /** Resumo do andamento da padronização; `null` enquanto não apurado. */
  readonly progress = signal<CompanyStandardizationProgress | null>(null);

  /**
   * A empresa tem algum documento contábil enviado? Separa "nada enviado" de "enviado e não validado".
   * `null` = não foi possível apurar (falhou ou ainda não voltou) — nesse caso a redação é a neutra, porque
   * afirmar "possui documentos enviados" sem ter verificado seria inventar.
   */
  readonly hasSentDocuments = signal<boolean | null>(null);

  /**
   * O resumo já respondeu ao menos uma vez (com sucesso OU com falha). Antes disso o cartão não escolhe
   * redação: `progress()` nulo cairia no ramo por documento enviado e poderia exibir "Nenhum documento
   * contábil enviado" — com botão para o Data Room — de uma empresa que está justamente extraindo.
   */
  readonly progressResolved = signal(false);

  /** Uma consulta manual ("Verificar agora") está em andamento — desabilita o botão para evitar rajada. */
  readonly isChecking = signal(false);

  /**
   * Permissão exigida para o botão "Verificar", que leva a "Padronização de dados contábeis". É a MESMA que
   * o `side-menu` usa para exibir aquele item — oferecer o atalho a quem não tem a tela no menu seria um
   * beco sem saída. (`USER_CREATE` é herança do menu, não escolha desta tela: se a padronização ganhar
   * permissão própria, troque nos três lugares — aqui, no `side-menu` e no `YearColumnMenuComponent`.)
   */
  readonly standardizationPermission = Permissions.User.CREATE;

  /** Permissão do Data Room, a mesma que o `side-menu` exige para exibir o item (oferecer sem ela seria beco sem saída). */
  readonly dataRoomPermission = Permissions.DataRoom.MODULE;

  /** O usuário tem o Data Room? Sem ele o cartão não promete um botão que não existe. */
  readonly canOpenDataRoom = toSignal(
    inject(PermissionsService).hasPermission(Permissions.DataRoom.MODULE),
    { initialValue: false }
  );

  ngOnChanges(): void {
    this.restart();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Reapura presença de documentos e andamento da padronização para a empresa atual. */
  private restart(): void {
    this.stopPolling();
    this.progress.set(null);
    this.hasSentDocuments.set(null);
    this.progressResolved.set(false);
    this.consecutiveFailures = 0;

    if (!this.companyId) return;

    this.loadSentDocumentsPresence();
    this.startPolling();
  }

  /**
   * Liga o polling do resumo. O `timer(0, …)` consulta imediatamente e repete a cada 10s **enquanto** houver
   * extração em voo: na primeira resposta com `isProcessing === false` o ciclo se encerra, porque a partir
   * daí nada muda por conta própria.
   *
   * O `catchError` fica DENTRO do `switchMap` de propósito. Se ele estivesse fora, o primeiro erro de rede
   * encerraria o `timer` e — como não há timeout — o usuário ficaria olhando "aparece em 5 a 10 minutos"
   * para sempre, sem nenhuma nova tentativa. Assim um blip é só um tick perdido.
   */
  private startPolling(): void {
    this.pollSubscription = timer(0, DfpEmptyStateComponent.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.standardizationService
            .getStandardizationProgress(this.companyId)
            .pipe(catchError(() => of(null)))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(progress => this.applyProgress(progress));
  }

  /**
   * Aplica a resposta do resumo. Quando a padronização estava em andamento e deixou de estar, avisa a tela
   * para recarregar — é o que faz os números aparecerem sem ação do usuário.
   */
  private applyProgress(progress: CompanyStandardizationProgress | null): void {
    this.isChecking.set(false);
    this.progressResolved.set(true);

    if (!progress) {
      // Consulta acessória: falhar não apaga a mensagem que já está em tela nem gera toast. Mas endpoint
      // quebrado também não pode virar polling eterno, então três falhas SEGUIDAS encerram o ciclo. Não é
      // um timeout de espera (esse o dono do produto dispensou) — é guarda de erro.
      this.consecutiveFailures += 1;
      if (this.consecutiveFailures >= DfpEmptyStateComponent.MAX_CONSECUTIVE_FAILURES) {
        this.stopPolling();
      }
      return;
    }

    this.consecutiveFailures = 0;

    const wasProcessing = this.progress()?.isProcessing === true;
    this.progress.set(progress);

    if (progress.isProcessing) return;

    this.stopPolling();

    if (wasProcessing) {
      this.standardizationFinished.emit();
    }
  }

  private stopPolling(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }

  /** Botão "Verificar agora": reinicia o ciclo, que consulta na hora (o `timer` começa em 0). */
  checkNow(): void {
    if (this.isChecking()) return;

    // Zera o contador: sem isto, depois de um blip que já tenha somado 3 falhas o ciclo encerraria na
    // PRIMEIRA falha de cada clique seguinte — o botão viraria uma tentativa só, para sempre.
    this.consecutiveFailures = 0;
    this.isChecking.set(true);
    this.stopPolling();
    this.startPolling();
  }

  /**
   * Apura se a empresa tem algum documento contábil enviado.
   *
   * A fonte é de propósito a MESMA lista da tela de "Padronização de dados contábeis"
   * (`accounting-document-management/documents`), e não os anexos que alimentam o painel de documentos de
   * origem: é para lá que o botão "Verificar" leva, e se a mensagem dissesse "nenhum documento enviado"
   * enquanto a lista de lá mostra documentos, a tela estaria se contradizendo com o próprio destino que
   * sugeriu. `pageSize: 1` porque só o `total` interessa.
   */
  private loadSentDocumentsPresence(): void {
    this.standardizationService
      .getDocuments({ companyIds: [this.companyId], page: 1, pageSize: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: page => this.hasSentDocuments.set((page?.total ?? 0) > 0),
        error: () => this.hasSentDocuments.set(null),
      });
  }

  /**
   * Empresa sem NENHUM documento contábil ainda não enviou nada ao **Data Room**, que é a entrada de documentos
   * (o Onboarding deixou de ser). Os documentos contábeis enviados lá são lidos e padronizados automaticamente;
   * a Padronização só extrai/revisa/valida o que já chegou.
   *
   * Abre `company-folders/<empresa>` com `?companyId=`, igual à lista de empresas: no escopo "Todas as empresas"
   * é do query param que a tela tira a empresa (Bug 3797).
   */
  goToDataRoom(): void {
    this.router.navigate(['/dashboard/market/data-room/company-folders', this.companyId], {
      queryParams: { companyId: this.companyId },
    });
  }

  /**
   * Leva à "Padronização de dados contábeis" já filtrada nesta empresa.
   *
   * A navegação (incluindo a gravação do filtro global de empresa, sem a qual a tela abre na empresa errada)
   * vive em `DfpStandardizationNavigationService`, compartilhada com o atalho "Ir até De/Para" do menu de
   * coluna de ano — o motivo de cada passo está documentado lá.
   */
  goToStandardization(): void {
    this.standardizationNavigation.goToList(this.companyId);
  }
}
